/**
 * "This receipt number was already used for this family" — a WARNING under
 * the Receipt # field on the staff payment forms. It never blocks: staff can
 * carry on and record the payment.
 *
 * Receipt numbers are typed by hand (a paper book, a staff member's
 * initials), so they repeat in ordinary use. Making them unique once blocked
 * a club from recording payments for six months. What stops a double click
 * recording twice is the form's attempt key (utils/attemptKey), not this.
 *
 * Usage:
 *   <ReceiptNumberWarning membershipId={invoice?.membership_id} receiptNumber={receiptNumber} />
 */
import React, { useEffect, useState } from "react";
import { checkReceiptNumber } from "../../api/paymentsAPI";

interface Props {
  membershipId?: string | null;
  receiptNumber?: string | null;
}

const WAIT_MS = 400;

const describe = (recordedAt?: string) => {
  if (!recordedAt) return "";
  const when = new Date(recordedAt);
  if (Number.isNaN(when.getTime())) return "";
  return ` on ${when.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
};

const ReceiptNumberWarning: React.FC<Props> = ({ membershipId, receiptNumber }) => {
  const [warning, setWarning] = useState<string | null>(null);

  useEffect(() => {
    const number = String(receiptNumber || "").trim();
    if (!membershipId || !number) {
      setWarning(null);
      return;
    }
    let current = true;
    // Wait for staff to stop typing before asking.
    const timer = setTimeout(async () => {
      const token = localStorage.getItem("authToken");
      const res = await checkReceiptNumber(token, membershipId, number);
      if (!current) return;
      if (res?.usedRecently) {
        const last = res.last;
        const amount = last && Number.isFinite(Number(last.amount)) ? ` for $${Number(last.amount).toFixed(2)}` : "";
        setWarning(
          `Receipt # ${number} was already used for this family${describe(last?.recordedAt)}${amount}. ` +
            "If this is a different payment you can carry on. If it is the same one, do not record it again."
        );
      } else {
        setWarning(null);
      }
    }, WAIT_MS);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [membershipId, receiptNumber]);

  if (!warning) return null;
  return (
    <p role="status" className="mt-1 text-sm text-amber-600" data-testid="receipt-number-warning">
      {warning}
    </p>
  );
};

export default ReceiptNumberWarning;
